import { formatCents, formatCount, parseMoneyToCents, tt } from '@agos/shared'

/**
 * Recording a delivery on `/stock`, and reading one off a photograph.
 *
 * `POST /stock/receipts` writes the delivery note, adds every line to `stock_items.qty` and records a
 * movement per line. `POST /stock/receipts/ocr` reads a photograph and answers
 * `[{name, qty, unit, unitPrice}]` — **names, not ids** — while the receipt endpoint needs a
 * `stockItemId` on every line.
 *
 * **So the matching is the whole problem, and it is where a goods receipt goes wrong quietly.** A line
 * that cannot be matched must not be dropped (the delivery is then understated and somebody's stock is
 * short) and must not be guessed (the wrong item is credited and two counts are wrong instead of one).
 * It is shown, unmatched, with the name the photograph gave, and the person recording the delivery picks
 * the item or leaves the line out deliberately.
 *
 * **The names in this data make that unavoidable.** Measured: 135 stock items, and *Golden Múka T650*
 * appears as **three separate items** (131, 133, 135). An exact-name match has three answers and no way
 * to choose between them, so a duplicate name is reported as ambiguous rather than resolved by taking the
 * first — which is what an implementation that sorts and picks would do.
 *
 * **OCR is not available on this stack and the screen says so.** `agos.ocr.api-key` resolves from
 * `AI_INTEGRATIONS_OPENAI_API_KEY` / `OPENAI_API_KEY`, neither of which is set, so `ReceiptOcr.read`
 * answers **503 — *"OCR nie je dostupné – chýba AI kľúč"***. Manual entry is the path that works, and the
 * upload says which it is rather than spinning.
 */

export interface StockItemLike {
  id?: number
  name?: string
  unit?: string
  active?: boolean
}

export interface OcrLine {
  name: string
  qty: number
  unit: string
  unitPriceCents: number
  /**
   * The stock item the PDF parser resolved through the supplier's own code — §9.135.
   *
   * Absent on a photographed line: a vision model has no supplier code to work from. Where present
   * it outranks the name match, because it is the supplier's identifier for the thing rather than a
   * guess at what two strings share.
   */
  resolvedStockItemId?: number | null
}

export function ocrLinesOf(payload: unknown): OcrLine[] {
  const rows = (payload as { lines?: unknown })?.lines
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const name = typeof row.name === 'string' ? row.name.trim() : ''
    if (name === '') return []
    const qty = typeof row.qty === 'number' ? row.qty : Number(row.qty ?? 0)
    return [
      {
        name,
        qty: Number.isFinite(qty) && qty > 0 ? qty : 0,
        unit: typeof row.unit === 'string' ? row.unit.trim() : '',
        unitPriceCents: parseMoneyToCents(row.unitPrice as string | number | null),
      },
    ]
  })
}

export type MatchKind = 'matched' | 'ambiguous' | 'unmatched'

export interface MatchedLine {
  line: OcrLine
  kind: MatchKind
  /** The chosen item, once there is exactly one candidate or somebody picked. */
  stockItemId: number | null
  candidates: StockItemLike[]
}

function normalise(value: string): string {
  return value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Match a photographed line to a stock item, by name.
 *
 * Exact normalised name first — accents and spacing folded, because a delivery note writes *"Múka T650"*
 * where the catalogue has *"Múka  T650"*. No fuzzy fallback: a near-match on a goods receipt credits the
 * wrong item's count, and the wrong count is discovered at stocktake, months later.
 */
export function matchLine(line: OcrLine, items: readonly StockItemLike[]): MatchedLine {
  const needle = normalise(line.name)
  const candidates = items.filter(
    (item) => item.active !== false && normalise(item.name ?? '') === needle,
  )
  if (candidates.length === 1) {
    return { line, kind: 'matched', stockItemId: candidates[0]?.id ?? null, candidates }
  }
  if (candidates.length > 1) {
    // Measured: "Golden Múka T650" is three items. Taking the first would credit an arbitrary one.
    return { line, kind: 'ambiguous', stockItemId: null, candidates }
  }
  return { line, kind: 'unmatched', stockItemId: null, candidates: [] }
}

export function matchAll(lines: readonly OcrLine[], items: readonly StockItemLike[]): MatchedLine[] {
  return lines.map((line) => matchLine(line, items))
}

/**
 * A parsed invoice PDF, read as receipt lines — §9.135.
 *
 * <p><b>Different keys and one extra fact.</b> `/invoices/parse-pdf` names the field `itemName` and
 * the price `unitPriceAfterDiscount`, and it carries a **`stockItemId`** the vision reader cannot:
 * the parser routes a line back to a stock item through that item's stored supplier codes. Where it
 * has done so, that beats matching on a name — it is the supplier's own code for the thing, not a
 * guess at what two strings have in common.
 *
 * <p>A line the parser could not resolve falls through to the same loose name match a photograph
 * gets, and an unresolved line is still returned so the delivery note is never silently shortened.
 */
export function pdfLinesOf(payload: unknown): OcrLine[] {
  const rows = (payload as { lines?: unknown })?.lines
  if (!Array.isArray(rows)) return []
  return rows.flatMap((entry) => {
    const row = entry as Record<string, unknown>
    const name = typeof row.itemName === 'string' ? row.itemName.trim() : ''
    if (name === '') return []
    const qty = typeof row.qty === 'number' ? row.qty : Number(row.qty ?? 0)
    return [
      {
        name,
        qty: Number.isFinite(qty) && qty > 0 ? qty : 0,
        unit: typeof row.unit === 'string' ? row.unit.trim() : '',
        unitPriceCents: parseMoneyToCents(
          row.unitPriceAfterDiscount as string | number | null,
        ),
        resolvedStockItemId: typeof row.stockItemId === 'number' ? row.stockItemId : null,
      },
    ]
  })
}

/**
 * Matches parsed lines, preferring the stock item the parser already resolved — §9.135.
 *
 * <p>The supplier's own code is a better answer than a name comparison, so where the parser gave one
 * it is used and the loose match is not consulted at all.
 */
export function matchAllParsed(
  lines: readonly OcrLine[],
  items: readonly StockItemLike[],
): MatchedLine[] {
  return lines.map((line) => {
    const resolved = line.resolvedStockItemId
    if (resolved === null || resolved === undefined) return matchLine(line, items)
    const known = items.find((item) => item.id === resolved)
    if (known === undefined || known.id === undefined) return matchLine(line, items)
    return { ...matchLine(line, items), stockItemId: known.id, kind: 'matched' as const }
  })
}

/** Said above the read lines, so nothing is silently missing from the delivery. */
export function matchNote(matched: readonly MatchedLine[]): string {
  if (matched.length === 0) return ''
  const counts = {
    matched: matched.filter((row) => row.stockItemId !== null).length,
    ambiguous: matched.filter((row) => row.kind === 'ambiguous' && row.stockItemId === null).length,
    unmatched: matched.filter((row) => row.kind === 'unmatched' && row.stockItemId === null).length,
  }
  const parts = [`${formatCount(counts.matched)} ${tt('priradených')}`]
  if (counts.ambiguous > 0) {
    parts.push(`${formatCount(counts.ambiguous)} ${tt('má rovnaký názov viackrát - vyberte ktorú')}`)
  }
  if (counts.unmatched > 0) {
    parts.push(`${formatCount(counts.unmatched)} ${tt('sa nenašlo v sklade - vyberte položku alebo riadok vynechajte')}`)
  }
  return `${parts.join(', ')}.`
}

export interface ReceiptForm {
  invoiceNumber: string
  supplier: string
  date: string
  notes: string
}

export const EMPTY_RECEIPT: ReceiptForm = { invoiceNumber: '', supplier: '', date: '', notes: '' }

/** What the server would refuse, said first. Its two messages, in its order. */
export function receiptProblems(form: ReceiptForm, matched: readonly MatchedLine[]): string[] {
  const usable = matched.filter((row) => row.stockItemId !== null)
  const found: string[] = []
  if (usable.length === 0) found.push(tt('Príjemka musí mať aspoň jednu položku'))
  if (usable.some((row) => row.line.qty <= 0)) found.push(tt('Množstvo musí byť kladné'))
  if (form.date.trim() !== '' && !/^\d{4}-\d{2}-\d{2}$/.test(form.date.trim())) {
    found.push(tt('Neplatný dátum'))
  }
  return found
}

/** The body `POST /stock/receipts` takes. Only the matched lines — the rest were left out on purpose. */
export function receiptPayload(
  form: ReceiptForm,
  matched: readonly MatchedLine[],
): Record<string, unknown> {
  return {
    invoiceNumber: form.invoiceNumber.trim(),
    supplier: form.supplier.trim(),
    ...(form.date.trim() === '' ? {} : { date: form.date.trim() }),
    ...(form.notes.trim() === '' ? {} : { notes: form.notes.trim() }),
    lines: matched
      .filter((row) => row.stockItemId !== null)
      .map((row) => ({
        stockItemId: row.stockItemId,
        qty: row.line.qty,
        unitPrice: row.line.unitPriceCents / 100,
      })),
  }
}

/** What this delivery is worth, so the total can be checked against the paper before it is recorded. */
export function receiptTotalCents(matched: readonly MatchedLine[]): number {
  return matched
    .filter((row) => row.stockItemId !== null)
    .reduce((total, row) => total + Math.round(row.line.unitPriceCents * row.line.qty), 0)
}

export function totalNote(matched: readonly MatchedLine[]): string {
  return `${tt('Spolu')} ${formatCents(receiptTotalCents(matched))}`
}

/** The client half of the server's own file rules, so a 30 MB photo fails here and not after the upload. */
export const OCR_TYPES = ['image/jpeg', 'image/png', 'image/webp']
export const PDF_TYPE = 'application/pdf'
export const OCR_MAX_BYTES = 20 * 1024 * 1024

/**
 * Which reader a dropped file goes to — §9.135, the owner's point 20.
 *
 * <p><b>A PDF used to be refused outright</b>, so the owner dropped in the document their supplier
 * emailed and was told to photograph it. The refusal had a real reason — `/stock/receipts/ocr` hands
 * bytes to a **vision** model — but it was never true that AGOS cannot read a PDF:
 * `/invoices/parse-pdf` extracts one locally, with no key and no metered call.
 *
 * <p>The two are not interchangeable and the screen says which ran: the parser reads text the
 * supplier wrote, the model guesses at pixels, and somebody checking a delivery against paper is
 * entitled to know which produced the rows.
 */
export type ReceiptReader = 'pdf' | 'photo'

export function readerFor(file: { type: string }): ReceiptReader | null {
  if (file.type === PDF_TYPE) return 'pdf'
  if (OCR_TYPES.includes(file.type)) return 'photo'
  return null
}

export function fileProblem(file: { type: string; size: number }): string | null {
  if (readerFor(file) === null) {
    return tt('Povolené sú iba obrázky (JPEG, PNG, WebP) alebo PDF')
  }
  if (file.size > OCR_MAX_BYTES) return tt('Súbor je príliš veľký (max 20 MB)')
  return null
}

/** Said above the rows, so nobody has to guess how much to trust them. */
export function readerNote(reader: ReceiptReader, lineCount: number): string {
  const read = `${lineCount} ${tt('položiek')}`
  return reader === 'pdf'
    ? `${tt('Načítané z PDF')} - ${read}. ${tt('Skontrolujte proti dokladu.')}`
    : `${tt('Rozpoznané z fotky')} - ${read}. ${tt('Skontrolujte proti dokladu.')}`
}

/**
 * Said when the server refuses because no AI key is configured.
 *
 * Measured on this stack: neither `AI_INTEGRATIONS_OPENAI_API_KEY` nor `OPENAI_API_KEY` is set, so every
 * upload answers 503. Manual entry is the path that works and the screen points at it rather than
 * leaving somebody to conclude the photograph was bad.
 */
export function ocrUnavailableNote(): string {
  return tt(
    'Čítanie z fotky nie je zapnuté - chýba kľúč k AI službe. Položky sa dajú zapísať ručne nižšie.',
  )
}
